import type {ReactNode} from 'react'
import {useRef, useState} from 'react'
import type {NavigateFunction} from 'react-router'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import {mthen, maybe, fromMaybe, mEffect} from '@freckle/maybe'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import {promiseObj} from '@freckle/educator-entities/ts/common/helpers/promise-obj'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {AuthenticatedFooter} from '@freckle/educator-materials/ts/components/footer/authenticated-footer'
import {SpinnerOverlay} from '@freckle/educator-materials/ts/components/spinner'
import {Tabs, Tab} from '@freckle/educator-materials/ts/components/tabs'
import {H2} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {PageWrapper} from '@freckle/school/ts/common/components/common/page-wrapper'
import {SchoolPicker} from '@freckle/school/ts/common/components/common/school-picker'
import {logErrorAsUnhandled} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {fetchSummary} from '@freckle/school/ts/common/models/summary'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import LicenseHeader from '@freckle/school/ts/manage-teachers/components/license-header'
import {Summary} from '@freckle/school/ts/manage-teachers/components/summary'
import {TooManySchoolsAlert} from '@freckle/school/ts/manage-teachers/components/too-many-schools-alert'
import {fetchLicenses} from '@freckle/school/ts/manage-teachers/models/license'
import type {LicenseAttrs} from '@freckle/school/ts/manage-teachers/models/license'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import type {FilteringParamsT, SharedTableParamsT, TableParamsT} from '../api-controlled-table'
import {CourseTabHandler as CourseTabContainer} from '../course-tab'
import RosteringBar from '../rostering-bar'
import {StudentTabHandler as StudentTabContainer} from '../student-tab'
import TeacherTabContainer from '../teacher-tab'
import type {TabKeyT} from './utils'
import {
  controlBar,
  manageTeachersWrapper,
  licensesWrapper,
  dashboardFooter
} from './manage-teachers.module.scss'

type Props = {
  schools: Array<SchoolAttrs>
  currentSchoolIds: Array<number> | undefined | null
  schoolAdmin: SchoolAdminAttrs
  routerHistory: NavigateFunction
  activeTab: TabKeyT | undefined | null
  tableParams: SharedTableParamsT
}

// Tabs break and show really broken errors to users when an admin has many
// schools. We won't fetch the tabs if the admin sees more than this many
// schools.
const DONT_SHOW_TABS_IF_ADMIN_HAS_MORE_SCHOOLS_THAN = 400

export function ManageTeachers({
  schools,
  currentSchoolIds,
  routerHistory,
  activeTab,
  schoolAdmin,
  tableParams
}: Props) {
  const tabsContainerElement = useRef<HTMLElement | undefined | null>(null)
  const oldCurrentSchoolIds = useRef(currentSchoolIds)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [licenses, setLicenses] = useState<Array<LicenseAttrs> | undefined | null>(null)
  const [summary, setSummary] = useState<SummaryT | undefined | null>(null)
  const {t} = useSafeTranslation()

  useSafeEffectExtraDeps<{updateCollectionsForCurrentSchool: () => void}>(
    ({updateCollectionsForCurrentSchool}) => {
      updateCollectionsForCurrentSchool()
    },
    [],
    {
      updateCollectionsForCurrentSchool: {
        value: updateCollectionsForCurrentSchool,
        comparator: () => true
      }
    }
  )

  useSafeEffectExtraDeps<{
    currentSchoolIds: Array<number> | undefined | null
    updateCollectionsForSchool: (schoolIds?: Array<number> | null) => void
  }>(
    ({currentSchoolIds, updateCollectionsForSchool}) => {
      if (!isEqual(oldCurrentSchoolIds.current, currentSchoolIds)) {
        updateCollectionsForSchool(currentSchoolIds)
        oldCurrentSchoolIds.current = currentSchoolIds
      }
    },
    [],
    {
      currentSchoolIds: {value: currentSchoolIds, comparator: isEqual},
      updateCollectionsForSchool: {value: updateCollectionsForSchool, comparator: () => true}
    }
  )

  function updateCollectionsForCurrentSchool() {
    updateCollectionsForSchool(currentSchoolIds)
  }

  function updateCollectionsForSchool(schoolIds?: Array<number> | null) {
    setIsLoading(true)

    if (schoolIds === null || schoolIds === undefined || schoolIds.length > 1) {
      const allSchoolIds = schools.map(s => s.id)
      const schoolIdsToUse = fromMaybe(() => allSchoolIds, schoolIds)
      const summaryFetch = fetchSummary(schoolAdmin.id, schoolIdsToUse)

      summaryFetch
        .then(summary => {
          setIsLoading(false)
          setSummary(summary)
        })
        .catch(err => logErrorAsUnhandled(new HttpError(err)))
    } else {
      mEffect(schoolIds[0], schoolId =>
        promiseObj({
          summary: fetchSummary(schoolAdmin.id, [schoolId]),
          licenses: fetchLicenses(schoolId)
        })
          .then(({summary, licenses}) => {
            setIsLoading(false)
            setLicenses(licenses)
            setSummary(summary)
          })
          .catch(err => logErrorAsUnhandled(new HttpError(err)))
      )
    }
  }

  function onSelectTab(tabKey: TabKeyT) {
    const url = Routes.dashboard({
      schoolIds: currentSchoolIds,
      activeTab: tabKey
    })
    routerHistory(url)
  }

  function onChangeSchool(schoolId: number) {
    const url = Routes.dashboard({
      schoolIds: [schoolId],
      activeTab
    })
    routerHistory(url)
  }

  function onAllSchools() {
    const url = Routes.dashboard({
      schoolIds: null,
      activeTab
    })
    routerHistory(url)
  }

  function scrollToTabs() {
    if (tabsContainerElement.current !== null && tabsContainerElement.current !== undefined) {
      tabsContainerElement.current.scrollIntoView()
    }
  }

  const selectedTab = fromMaybe(() => 'teacher-tab', activeTab)

  if (isLoading || summary === null || summary === undefined) {
    return <SpinnerOverlay />
  }

  const hasSelectedOnlyOneSchool =
    currentSchoolIds !== null && currentSchoolIds !== undefined && currentSchoolIds.length === 1

  const allSchoolIds = schools.map(s => s.id)
  const hasActiveIdp = schools.some(s => s.activeIdp !== null && s.activeIdp !== undefined)
  const hasRGPIdp = schools.some(s => s.activeIdp === 'renaissance')
  const schoolIdsToUse = fromMaybe(() => allSchoolIds, currentSchoolIds)

  const firstSchoolId = schoolIdsToUse[0]

  return (
    <div className="manage-teachers">
      <PageWrapper title={t('MANAGE_TEACHERS_PAGE_TITLE')}>
        <div className={controlBar}>
          {firstSchoolId !== undefined ? (
            <SchoolPicker
              schools={schools}
              currentSchoolId={firstSchoolId}
              onChange={onChangeSchool}
              onAllSchools={onAllSchools}
              showingAllSchools={!hasSelectedOnlyOneSchool}
            />
          ) : null}
          <RosteringBar summary={summary} hasActiveIdp={hasActiveIdp} />
        </div>

        <SchoolTitle schools={schools} schoolIds={currentSchoolIds} />

        <Summary
          currentSchoolIds={currentSchoolIds}
          summary={summary}
          scrollToTabs={scrollToTabs}
        />

        {!isNil(licenses) && !isNil(currentSchoolIds) ? (
          <LicensePanel schools={schools} currentSchoolIds={currentSchoolIds} licenses={licenses} />
        ) : null}

        <div className={manageTeachersWrapper}>
          <H2>{t('MANAGE_TEACHERS_MANAGE_ROSTER_HEADER')}</H2>
          <div
            ref={el => {
              tabsContainerElement.current = el
            }}
            className="row"
          >
            <div className="col-12">
              {schools.length > DONT_SHOW_TABS_IF_ADMIN_HAS_MORE_SCHOOLS_THAN ? (
                <TooManySchoolsAlert />
              ) : null}
              {schools.length <= DONT_SHOW_TABS_IF_ADMIN_HAS_MORE_SCHOOLS_THAN ? (
                <Tabs
                  id="admin-dashboard-tabs"
                  expandTabs
                  activeKey={selectedTab}
                  onSelect={onSelectTab as (key: string | null) => void}
                >
                  <Tab title={t('MANAGE_TEACHERS_TEACHER_TAB_TITLE')} eventKey="teacher-tab">
                    <TeacherTabContainer
                      schools={schools}
                      currentSchoolIds={currentSchoolIds}
                      tableParams={toFilteringParams(tableParams)}
                      schoolAdmin={schoolAdmin}
                      summary={summary}
                      onUpdateTeachers={updateCollectionsForCurrentSchool}
                      hasRGPIdp={hasRGPIdp}
                    />
                  </Tab>
                  <Tab title={t('MANAGE_TEACHERS_STUDENTS_TAB_TITLE')} eventKey="student-tab">
                    <StudentTabContainer
                      schools={schools}
                      currentSchoolIds={currentSchoolIds}
                      routerHistory={routerHistory}
                      tableParams={toFilteringParams(tableParams)}
                      summary={summary}
                    />
                  </Tab>
                  <Tab title={t('MANAGE_TEACHERS_COURSES_TAB_TITLE')} eventKey="course-tab">
                    <CourseTabContainer
                      schools={schools}
                      currentSchoolIds={currentSchoolIds}
                      routerHistory={routerHistory}
                      tableParams={toFilteringParams(tableParams)}
                      summary={summary}
                    />
                  </Tab>
                </Tabs>
              ) : null}
            </div>
          </div>
        </div>
      </PageWrapper>
      <div className={dashboardFooter}>
        <AuthenticatedFooter />
      </div>
    </div>
  )
}

const LicensePanel = ({
  schools,
  currentSchoolIds,
  licenses
}: {
  schools: Array<SchoolAttrs>
  currentSchoolIds: Array<number>
  licenses: Array<LicenseAttrs>
}): ReactNode => {
  if (currentSchoolIds.length !== 1) {
    return null
  }

  return mthen(currentSchoolIds[0], currentSchoolId =>
    mthen(
      schools.find(school => school.id === currentSchoolId),
      currentSchool => (
        <div className={licensesWrapper}>
          <H2>
            <SafeTrans i18nKey="MANAGE_TEACHERS_LICENSE_PANEL_HEADER" />
          </H2>
          <LicenseHeader licenses={licenses} currentSchool={currentSchool} />
        </div>
      )
    )
  )
}

type SchoolTitleProps = {
  schools: Array<SchoolAttrs>
  schoolIds: Array<number> | undefined | null
}

function SchoolTitle({schools, schoolIds}: SchoolTitleProps) {
  const {t} = useSafeTranslation()

  const firstSchool = mthen(schoolIds, ids => schools.find(s => ids.includes(s.id)))

  if (schoolIds === null || schoolIds === undefined || schoolIds.length > 1 || isNil(firstSchool)) {
    const numSchools =
      schoolIds !== null && schoolIds !== undefined ? schoolIds.length : schools.length
    return <H2>{t('MANAGE_TEACHERS_SCHOOL_TITLE_ALL_SCHOOLS_HEADER', {numSchools})}</H2>
  }

  const sisIdNode = maybe(
    () => null,
    sisId => <small>{t('MANAGE_TEACHERS_SCHOOL_TITLE_SIS_ID', {sisId})}</small>,
    firstSchool.sisId
  )

  return (
    <H2>
      {firstSchool.name} {sisIdNode}
    </H2>
  )
}

function toFilteringParams<Filter extends FilteringParamsT>(
  params: SharedTableParamsT
): TableParamsT<Filter> {
  return {...params}
}
